/**
 * Video Platform Presets (frontend mirror of backend).
 * Keep in sync with /app/backend/app/services/video_platform_presets.py.
 *
 * Each preset auto-fills aspect ratio, duration, model, and on-screen text
 * preference. Users can still tweak any field manually after picking.
 */

export const PRESET_REELS_TIKTOK = 'reels_tiktok';
export const PRESET_YOUTUBE_SHORTS = 'youtube_shorts';
export const PRESET_YOUTUBE_AD = 'youtube_ad';
export const PRESET_FACEBOOK_AD = 'facebook_ad';
export const PRESET_INSTAGRAM_FEED = 'instagram_feed';
export const PRESET_CUSTOM = 'custom';

export const VIDEO_PRESETS = [
  {
    key: PRESET_REELS_TIKTOK,
    label: 'Reels & TikTok',
    emoji: '📱',
    aspect_ratio: '9:16',
    duration: 8,
    model: 'gemini_omni_video',
    render_text: true,
    description: 'Vertical short-form ad for Instagram Reels and TikTok.',
    badge: 'Ultra Pro',
  },
  {
    key: PRESET_YOUTUBE_SHORTS,
    label: 'YouTube Shorts',
    emoji: '🎬',
    aspect_ratio: '9:16',
    duration: 10,
    model: 'gemini_omni_video',
    render_text: true,
    description: 'Vertical video — 10-second sweet spot for Shorts.',
    badge: 'Ultra Pro',
  },
  {
    key: PRESET_YOUTUBE_AD,
    label: 'YouTube Ad',
    emoji: '📺',
    aspect_ratio: '16:9',
    duration: 8,
    model: 'gemini_omni_video',
    render_text: true,
    description: 'Landscape pre-roll with crisp on-screen text rendering.',
    badge: 'Ultra Pro',
  },
  {
    key: PRESET_FACEBOOK_AD,
    label: 'Facebook Video',
    emoji: '👥',
    aspect_ratio: '1:1',
    duration: 8,
    model: 'gemini_omni_video',
    render_text: true,
    description: 'Square video — top format in Meta Ads Manager.',
    badge: 'Ultra Pro',
  },
  {
    key: PRESET_INSTAGRAM_FEED,
    label: 'Instagram Feed',
    emoji: '🟦',
    aspect_ratio: '1:1',
    duration: 8,
    model: 'gemini_omni_video',
    render_text: true,
    description: 'Square feed video. Optimised for engagement.',
    badge: 'Ultra Pro',
  },
  {
    key: PRESET_CUSTOM,
    label: 'Custom',
    emoji: '⚙️',
    aspect_ratio: '16:9',
    duration: 8,
    model: 'veo3_fast',
    render_text: false,
    description: 'Choose every setting manually.',
    badge: 'Manual',
  },
];

/**
 * Apply preset onto a formData object. Returns a NEW object (no mutation).
 * For PRESET_CUSTOM (or unknown), simply sets the platform key without
 * touching other fields — user keeps full manual control.
 */
export function applyVideoPreset(formData, presetKey) {
  if (!presetKey) return { ...formData };
  if (presetKey === PRESET_CUSTOM) {
    return { ...formData, video_platform_preset: PRESET_CUSTOM };
  }
  const preset = VIDEO_PRESETS.find((p) => p.key === presetKey);
  if (!preset) return { ...formData, video_platform_preset: presetKey };

  // Choose resolution that's compatible with the chosen model.
  // Only auto-bump UP — if the user picked a higher tier manually, keep it.
  // 720p is supported by every model, so it's never auto-overridden.
  let resolution = formData.video_resolution || '720p';
  if (!resolution || resolution === '') resolution = '720p';

  return {
    ...formData,
    video_platform_preset: preset.key,
    video_aspect_ratio: preset.aspect_ratio,
    video_duration: preset.duration,
    video_model: preset.model,
    render_text_in_video: preset.render_text,
    video_resolution: resolution,
  };
}


/**
 * Map Campaign Strategy `primary_platform` value → recommended video preset key.
 * Used to auto-suggest a tile in the VideoPlatformPicker so users don't have
 * to make the same platform choice twice. The user can always override by
 * clicking a different tile.
 */
export const PLATFORM_TO_VIDEO_PRESET = {
  instagram: PRESET_INSTAGRAM_FEED,
  facebook: PRESET_FACEBOOK_AD,
  tiktok: PRESET_REELS_TIKTOK,
  youtube: PRESET_YOUTUBE_AD,
  linkedin: PRESET_FACEBOOK_AD,      // Square video plays well on LinkedIn feed
  google_display: PRESET_YOUTUBE_AD, // 16:9 landscape is the GDN default
};

/** Pretty display label for primary_platform values. */
export const PRIMARY_PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  google_display: 'Google Display',
};

export function getPlatformLabel(primaryPlatform) {
  if (!primaryPlatform) return null;
  return (
    PRIMARY_PLATFORM_LABELS[primaryPlatform] ||
    primaryPlatform.charAt(0).toUpperCase() + primaryPlatform.slice(1).replace(/_/g, ' ')
  );
}

export function getSuggestedPresetForPlatform(primaryPlatform) {
  if (!primaryPlatform) return null;
  return PLATFORM_TO_VIDEO_PRESET[primaryPlatform] || null;
}
